import React, { useState } from 'react';
import { Input } from 'components/ui/input';
import { Button as MuiButton, Grid, CircularProgress } from '@mui/material';
import styles from './ScreenGroupForm.module.scss';
import screenStyles from './ScreenSetting.module.scss';
import { useQueryClient } from 'react-query';
import { ScreenGroup } from 'types';
import { updateScreenGroup } from 'core/API/ScreenSetting';
import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';

interface EditScreenGroupSettingsProps {
    screenGroup: ScreenGroup;
    screenGroups: ScreenGroup[];
    handleBackClick: () => void;
    onScreenGroupUpdated?: () => void;
}

const EditScreenGroupSettings: React.FC<EditScreenGroupSettingsProps> = ({
    screenGroup,
    screenGroups,
    handleBackClick,
    onScreenGroupUpdated,
}) => {
    const { workplaceId } = useWorkplacesContext();
    const [editLabel, setEditLabel] = useState(screenGroup?.label || '');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const queryClient = useQueryClient();

    if (!screenGroup) {
        return <div className={styles.loading}><CircularProgress size={20} /></div>;
    }

    // Check for duplicate label (excluding current screen group)
    const isDuplicate = screenGroups.some(
        (g: ScreenGroup) => g.id !== screenGroup.id && g.label.toLowerCase() === editLabel.toLowerCase()
    );

    const handleUpdateScreenGroup = async () => {
        setError(null);
        setIsLoading(true);

        if (!editLabel.trim()) {
            setError('Please enter a screen group label.');
            setIsLoading(false);
            return;
        }
        if (isDuplicate) {
            setError('This screen group label already exists.');
            setIsLoading(false);
            return;
        }

        try {
            await updateScreenGroup({
                screenGroupID: screenGroup.id.toString(),
                data: {
                    label: editLabel.trim(),
                    workplace: screenGroup.workplace,
                    list_order: screenGroup.list_order,
                    deleted: screenGroup.deleted,
                },
            });
            await queryClient.invalidateQueries(['screenGroups', workplaceId]);
            await queryClient.invalidateQueries(['screens']);
            await queryClient.invalidateQueries(['gamemaster-planner', workplaceId]);
            onScreenGroupUpdated?.();
            handleBackClick();
        } catch (error: any) {
            console.error('Error updating screen group:', error);
            setError(error?.response?.data?.message || error?.message || 'Failed to update screen group. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const isFormValid = editLabel.trim() && !isDuplicate;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleUpdateScreenGroup();
    };

    return (
        <div className={screenStyles.createScreenGroupSetting}>
            <div className={screenStyles.formContainer}>
                <Grid container direction="column" spacing={2} pl={2}>
                    <Grid container direction="row" justifyContent="space-between">
                        <Grid>
                            <MuiButton className={screenStyles.backButton} onClick={handleBackClick}>
                                Back
                            </MuiButton>
                        </Grid>
                        <Grid item xs={6} display={'flex'} justifyContent="flex-end">
                            <Grid>
                                <MuiButton
                                    className={screenStyles.saveButton}
                                    onClick={handleSubmit}
                                    disabled={isLoading || !isFormValid}
                                >
                                    {isLoading ? 'Saving...' : 'Save'}
                                </MuiButton>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid xs={12} container direction="row" justifyContent="flex-start" alignItems="center">
                        <h2 className={screenStyles.formTitle}>Edit Screen Group</h2>
                    </Grid>
                </Grid>

                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className={screenStyles.errorMessage}>
                            {error}
                        </div>
                    )}

                    <div className={styles.formRow}>
                        <div className={styles.formField}>
                            <label className={styles.label}>Screen Group</label>
                            <Input
                                type="text"
                                required
                                placeholder="Screen Group"
                                value={editLabel}
                                onChange={(e) => setEditLabel(e.target.value)}
                                className={styles.input}
                            />
                            {isDuplicate && (
                                <span className={styles.fieldError}>This screen group label already exists.</span>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditScreenGroupSettings;

